import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Grid, MenuItem, styled, Stack, Button, CircularProgress } from '@mui/material';
import { useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { FormProvider, useForm } from 'react-hook-form';
import {
  saveClientBank,
  ClientBankDTO,
  IClientCurrencySettings,
  saveActivityLog,
  LogEntityType,
  LogAction,
  LogContextType,
} from 'wisestep-api';
import { ControlledTextInput, Label, WsButton, WsColors } from 'wisestep-base';
import { object, string } from 'yup';

import { SelectWCreate } from '../../../../components/SelectWCreate';
import {
  getAvEditAddDesUiName,
  activityLogDTOHelper,
} from '../../../../features/activityLog/ActivityHelper';
import { useDialog } from '../../../../hooks/useDialog';
import ErrorLogger from '../../../../providers/ErrorLogger';
import { failed, success } from '../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../utils/NotificationStore';
import CurrencyForm from '../../../clientsSettings/components/CurrencyForm';
import IClientBank from '../../types/IClientBank';
const validationSchema = object().shape({
  bankName: string().required('Please provide Bank Name'),
  branchName: string().required('Please provide Branch Name'),
  bankCode: string().required('Please provide Bank Code'),
  bankAccountNumber: string().required('Please provide Account Number'),
  currencyId: string().required('Please select Currency'),
});

const DrawerFooter = styled(Grid)(() => ({
  position: 'fixed',
  bottom: 1,
  justifyContent: 'flex-start',
  background: WsColors.TertiaryShades100,
  width: '50vw',
  marginLeft: '-0.8vw',
}));
interface ClientBankInfoFormProps {
  index: number;
  clientBank: IClientBank;
  currencyList: IClientCurrencySettings[];
  getCurrencyData: () => Promise<void>;
  showModal?: () => void;
  editFlag?: boolean;
  loadTabData?: (pageNo: number) => void;
}

const ClientBankInfoForm = ({
  index,
  clientBank,
  currencyList = [],
  getCurrencyData,
  showModal,
  loadTabData,
  editFlag = false,
}: ClientBankInfoFormProps) => {
  const [openDialog, closeDialog] = useDialog();
  const methods = useForm<any>({
    resolver: yupResolver(validationSchema),
    defaultValues: clientBank.data,
  });

  const {
    handleSubmit,
    setValue,
    watch,
    formState: { dirtyFields },
  } = methods;

  const bankName = watch(`b
  ankName`);
  const branchName = watch(`branchName`);
  const bankCode = watch(`bankCode`);
  const bankAccountNumber = watch(`bankAccountNumber`);
  const currencyId = watch(`currencyId`);

  const setNotification = useNotificationStore((state) => state.setNotification);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setValue(`bankName`, clientBank?.data.BankName);
    setValue(`branchName`, clientBank?.data.branchName);
    setValue(`bankCode`, clientBank?.data.bankCode);
    setValue(`bankAccountNumber`, clientBank?.data.accountNumber);
    setValue(`currencyId`, clientBank?.data.currencyId);
    setValue(`iban`, clientBank?.data.iban);
    setValue(`swiftCode`, clientBank?.data.swiftCode);
  }, [clientBank, setValue]);

  useEffect(() => {
    clientBank.data.bankName = bankName;
    clientBank.data.branchName = branchName;
    clientBank.data.bankCode = bankCode;
    clientBank.data.bankAccountNumber = bankAccountNumber;
    clientBank.data.currencyId = currencyId;
  }, [clientBank, bankName, branchName, bankCode, bankAccountNumber, currencyId]);

  const saveData = async (data: ClientBankDTO) => {
    try {
      const response = await saveClientBank(data);
      setNotification(success('Legal identifiers has been saved successfully !!'));
      return response.data.data;
    } catch {
      setNotification(failed('Something went wrong, Please try again !!'));
    }
  };

  const onSubmit = async (data) => {
    const avEmptyEntityfields = Object.keys(
      Object.entries(clientBank.data).reduce((acc, [k, v]) => {
        if (v === '' || v === null) acc[k] = v;
        return acc;
      }, {})
    );
    setIsLoading(true);
    clientBank.data.bankName = data.bankName;
    clientBank.data.branchName = data.branchName;
    clientBank.data.bankCode = data.bankCode;
    clientBank.data.bankAccountNumber = data.bankAccountNumber;
    clientBank.data.currencyId = data.currencyId;
    clientBank.data.iban = data.iban;
    clientBank.data.swiftCode = data.swiftCode;
    clientBank.isEditable = false;
    clientBank.data = await saveData(clientBank.data);
    const avPayload = { ...activityLogDTOHelper };
    const avChangedValueViaDirtyFileds = Object.keys(dirtyFields).toString().split(',');
    if (data.id) {
      avPayload.action = LogAction.Edit;
      avPayload.description = `${getAvEditAddDesUiName(
        'Client',
        avEmptyEntityfields,
        avChangedValueViaDirtyFileds
      )} for Bank Details ${clientBank.data.bankName} under`;
    } else {
      avPayload.action = LogAction.Add;
      avPayload.description = `ADDED ${clientBank.data.bankName} for Bank Details under`;
    }
    avPayload.entityType = LogEntityType.Bank;
    avPayload.parentEntitiyType = LogContextType.Client;
    avPayload.parentEntitiyId = data.clientId;
    avPayload.entityId = clientBank.data.id;
    avPayload.entityName = clientBank.data.bankName;
    avPayload.field = `Bank Details`;
    avPayload.contextData = { clientId: data.clientId };
    avChangedValueViaDirtyFileds[0].length > 0 ? await saveActivityLog(avPayload) : '';
    console.log('hello');
    showModal && showModal();
    loadTabData && loadTabData(0);
    setIsLoading(false);
  };

  return (
    <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
      <FormProvider {...methods} key={index}>
        <Box component="form" onSubmit={handleSubmit((data) => onSubmit(data))}>
          <Grid container spacing={2} sx={{ px: 3, pt: 1, pb: 12 }}>
            <Grid item xs={12} sm={6}>
              <Label>Bank Name *</Label>
              <ControlledTextInput
                name={`bankName`}
                type="text"
                fullWidth
                label="Bank Name"
                variant="outlined"
                showMandatorySymbol={true}
                disabled={!clientBank.isEditable}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Label>Branch Name *</Label>
              <ControlledTextInput
                name={`branchName`}
                type="text"
                fullWidth
                label="Branch Name"
                variant="outlined"
                showMandatorySymbol={true}
                disabled={!clientBank.isEditable}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Label>Bank Code *</Label>
              <ControlledTextInput
                name={`bankCode`}
                type="text"
                fullWidth
                label="Bank Code"
                variant="outlined"
                showMandatorySymbol={true}
                disabled={!clientBank.isEditable}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Label>Bank Account Number *</Label>
              <ControlledTextInput
                name={`bankAccountNumber`}
                type="text"
                fullWidth
                label="Bank Account Number"
                variant="outlined"
                showMandatorySymbol={true}
                disabled={!clientBank.isEditable}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Label>International Bank Number</Label>
              <ControlledTextInput
                name={`iban`}
                type="text"
                fullWidth
                label="International Bank Number"
                variant="outlined"
                showMandatorySymbol={true}
                disabled={!clientBank.isEditable}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Label>Swift Code</Label>
              <ControlledTextInput
                name={`swiftCode`}
                type="text"
                fullWidth
                label="Swift Code"
                variant="outlined"
                showMandatorySymbol={true}
                disabled={!clientBank.isEditable}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Label>Currency *</Label>
              <SelectWCreate
                onCreate={() => {
                  openDialog({
                    onClose: () => {
                      setValue(`currencyId`, null);
                    },
                    children: (
                      <CurrencyForm
                        onSave={async (savedCurrency) => {
                          await getCurrencyData();
                          closeDialog();
                          setValue('currencyId', savedCurrency.id);
                        }}
                        onCancel={() => {
                          closeDialog();
                        }}
                      />
                    ),
                  });
                }}
                placeholder="Currency"
                name={`currencyId`}
                disabled={!clientBank.isEditable}
                defaultValue={clientBank.data?.currencyId ?? ''}
              >
                {currencyList.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.symbol}
                  </MenuItem>
                ))}
              </SelectWCreate>
            </Grid>
          </Grid>
          <DrawerFooter>
            <Stack direction="row" justifyContent="flex-end" m={2} spacing={2}>
              <Button
                size="large"
                color="error"
                onClick={() => {
                  loadTabData && loadTabData(0);
                  showModal && showModal();
                }}
              >
                {`Cancel`}
              </Button>
              <WsButton
                type="submit"
                variant="contained"
                size="large"
                disabled={isLoading}
                endIcon={isLoading ? <CircularProgress size={17} color="secondary" /> : undefined}
              >
                {isLoading ? 'Saving...' : editFlag ? 'Update' : `Save`}
              </WsButton>
            </Stack>
          </DrawerFooter>
        </Box>
      </FormProvider>
    </ErrorBoundary>
  );
};

export default ClientBankInfoForm;
